import { useCallback, useEffect, useRef, useState } from "react";
import useGraphql from "../../Hooks/useGraphql";
import { getStorage } from "../../auth/utils";
import { readStaffStorageScope } from "./staffUtils";

export const LOOKUP_STAFF_ACCOUNT = `
  query LookupStaffAccount($clubId: String!, $identity: String!) {
    lookupStaffAccount(clubId: $clubId, identity: $identity) {
      status account { _id firstName lastName }
    }
  }
`;

export const staffLookupIdentities = (email, mobile) => {
  const normalizedEmail = (email || "").trim().toLowerCase();
  const normalizedMobile = (mobile || "").replace(/\s/g, "");
  if (normalizedEmail && (normalizedEmail.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail))) return null;
  if (normalizedMobile && !/^(?:\+3069\d{8}|\+3579\d{7})$/.test(normalizedMobile)) return null;
  const identities = [normalizedEmail, normalizedMobile].filter(Boolean);
  return identities.length ? identities : null;
};

export default function useStaffAccountLookup({ enabled, email, mobile, clubId, storageScope, expectedFirebaseUid }) {
  const { axiosGraphQL } = useGraphql();
  const request = useRef(axiosGraphQL);
  request.current = axiosGraphQL;
  const [revision, setRevision] = useState(0);
  const currentRevision = useRef(0);
  const refresh = useCallback(() => {
    currentRevision.current += 1;
    setRevision(currentRevision.current);
  }, []);
  const key = JSON.stringify([enabled, email, mobile, clubId, storageScope, expectedFirebaseUid, revision]);
  const [result, setResult] = useState(null);
  useEffect(() => {
    setResult(null);
    const identities = staffLookupIdentities(email, mobile);
    if (!enabled || !clubId || !expectedFirebaseUid || !identities) return undefined;
    let active = true;
    const isCurrent = () => active && currentRevision.current === revision &&
      readStaffStorageScope(getStorage()) === storageScope &&
      getStorage().getItem("firebaseUid") === expectedFirebaseUid;
    const timer = setTimeout(async () => {
      if (!isCurrent()) return;
      try {
        const responses = await Promise.all(identities.map(identity =>
          request.current({ query: LOOKUP_STAFF_ACCOUNT, variables: { clubId, identity } }, { expectedFirebaseUid })
        ));
        if (!isCurrent()) return;
        const matches = responses.map(response => response?.data?.data?.lookupStaffAccount);
        if (responses.some(response => response?.data?.errors?.length) ||
            matches.some(match => !["FOUND", "NOT_FOUND", "CONFLICT"].includes(match?.status) ||
              (match.status === "FOUND" && !match.account?._id))) throw new Error();
        const found = matches.filter(match => match.status === "FOUND");
        const conflict = matches.some(match => match.status === "CONFLICT") ||
          (found.length > 0 && (found.length !== matches.length || found.some(match => match.account._id !== found[0].account._id)));
        setResult({ key, status: conflict ? "CONFLICT" : found.length ? "FOUND" : "NOT_FOUND",
          account: !conflict && found.length ? found[0].account : null, identity: identities[0] });
      } catch (_) {
        if (isCurrent()) setResult({ key, status: "ERROR" });
      }
    }, 400);
    return () => { active = false; clearTimeout(timer); };
  }, [key, enabled, email, mobile, clubId, storageScope, expectedFirebaseUid, revision]);
  if (result?.key === key) return { ...result, refresh };
  return { key, status: enabled && staffLookupIdentities(email, mobile) ? "LOADING" : "IDLE", refresh };
}
